import type {ChartData} from "@mantine/charts";
import {Area, AreaChart as AreaChartRecharts, CartesianGrid, Label, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import {AreaGradient, getNormalisedYAxisLabelsFromChartSeries} from "~/components/reports/widgets/comboChart";
import type {ChartSeries} from "~/components/reports/widgetSpecific/types";
import {graphColors} from "~/constants/graphColors";
import {numberToHumanFriendlyStringWithNumberSystem} from "~/utilities/utilities";
import {NumberSystems} from "~/utilities/typeDefinitions";
import {graphsXAxisTickFormatter} from "~/components/reports/utilities";
import {useAnankePreferences} from "@vesta/ananke/contexts/anankeClientContext";

export function AreaChart({
    chartData,
    chartSeries,
    xAxisKey,
    xAxisTitle,
    height,
    maxYValue,
    maxYValueSecondary,
}: {
    chartData: ChartData;
    chartSeries: Array<ChartSeries>;
    xAxisKey: string;
    xAxisTitle: string;
    scalingFactor: number;
    height: number;
    maxYValue: number;
    maxYValueSecondary: number | null;
}) {
    const yAxisLabels = getNormalisedYAxisLabelsFromChartSeries(chartSeries);
    const {theme} = useAnankePreferences();

    return (
        <ResponsiveContainer
            width="100%"
            height={height}
        >
            <AreaChartRecharts
                data={chartData}
                margin={{top: 20, right: 20, bottom: 20, left: 20}}
            >
                <XAxis
                    dataKey={xAxisKey}
                    scale="auto"
                    tickFormatter={(value) => graphsXAxisTickFormatter(value)}
                    tickMargin={20}
                    tickSize={0}
                    tick={{
                        fontSize: "10px",
                    }}
                    axisLine={false}
                >
                    <Label
                        value={xAxisTitle}
                        dy={28}
                        style={{
                            textAnchor: "middle",
                            fontSize: "10px",
                        }}
                    />
                </XAxis>

                <CartesianGrid
                    horizontal={true}
                    vertical={false}
                    stroke={theme === "dark" ? "#ADACAC" : "#262626"}
                    strokeDasharray="7 5"
                />

                {yAxisLabels.left !== null && (
                    <YAxis
                        yAxisId={"left"}
                        orientation={"left"}
                        tickFormatter={(value) => {
                            return `${numberToHumanFriendlyStringWithNumberSystem(value, NumberSystems.indian)}`;
                        }}
                        tickSize={0}
                        tickMargin={4}
                        tick={{
                            fontSize: "10px",
                        }}
                        axisLine={false}
                        domain={[0, maxYValue]}
                    >
                        <Label
                            value={yAxisLabels.left.join(", ")}
                            position={"insideLeft"}
                            offset={0}
                            angle={-90}
                            style={{
                                textAnchor: "middle",
                                fontSize: "10px",
                            }}
                        />
                    </YAxis>
                )}

                {yAxisLabels.right !== null && (
                    <YAxis
                        yAxisId={"right"}
                        orientation={"right"}
                        tickFormatter={(value) => {
                            return `${numberToHumanFriendlyStringWithNumberSystem(value, NumberSystems.indian)}`;
                        }}
                        tickSize={0}
                        tickMargin={4}
                        tick={{
                            fontSize: "10px",
                        }}
                        axisLine={false}
                        domain={maxYValueSecondary === null ? undefined : [0, maxYValueSecondary]}
                    >
                        <Label
                            value={yAxisLabels.right.join(", ")}
                            position={"insideRight"}
                            offset={0}
                            angle={90}
                            style={{
                                textAnchor: "middle",
                                fontSize: "10px",
                            }}
                        />
                    </YAxis>
                )}

                <Tooltip
                    contentStyle={{
                        color: theme === "dark" ? "#C7C7C7" : "#292a2e",
                        background: theme === "dark" ? "#1F1F1F" : "#ffffff",
                        border: theme === "dark" ? "1px solid #383838" : "1px solid #0b120e24",
                        borderRadius: "4px",
                        fontSize: "14px",
                    }}
                    labelFormatter={(value) => {
                        return graphsXAxisTickFormatter(value);
                    }}
                    cursor={{
                        fill: theme === "dark" ? "#2B2B2B" : "#E5E5E5",
                    }}
                    formatter={(value) => {
                        const number = parseFloat(String(value));
                        return Number.isInteger(number) === true ? number : number.toFixed(2);
                    }}
                />

                <Legend
                    verticalAlign="top"
                    align="right"
                    wrapperStyle={{
                        top: "-4px",
                        fontSize: "10px",
                    }}
                />

                <defs>
                    {chartSeries.map((series, seriesIndex) => {
                        return (
                            <AreaGradient
                                color={series.color}
                                fillOpacity={0.4}
                                id={series.color.replaceAll("#", "")}
                                withGradient={true}
                                key={seriesIndex}
                            />
                        );
                    })}
                </defs>

                {chartSeries.map((series, seriesIndex) => {
                    return (
                        <Area
                            key={seriesIndex}
                            dataKey={series.name}
                            fill={`url(#${series.color.replaceAll("#", "")})`}
                            stroke={graphColors[seriesIndex % graphColors.length]}
                            yAxisId={series.yAxisId}
                            type="bump"
                            dot={false}
                            strokeWidth={2}
                            activeDot={{
                                radius: 1,
                                strokeWidth: 0,
                            }}
                            legendType="plainline"
                        />
                    );
                })}
            </AreaChartRecharts>
        </ResponsiveContainer>
    );
}
